import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  DEFAULT_ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_OPTIONS,
} from "../../../constants/app";
import { timesheetFilterOptions } from "../../../models/vacancy";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { useAppSelector, useAppDispatch as useDispatch } from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { PaginationLink } from "../../../store/superadmin/types";
import { getCandidateActiveTimeSheet } from "../../../store/timesheet/thunks";
import { TTimesheetVacancyData } from "../../../store/timesheet/types";
import PaginationControls from "../../../utils/PaginationControls";
import RoundedTick from "../../UI/Icons/RoundedTick";
import SortBy from "../../UI/Icons/SortBy";
import Warning from "../../UI/Icons/Warning";
import SortFilterDropdown, { SortOrder } from "../../UI/SortFilterDropdown";
import ActiveTimeSheetListingCard from "./ActiveTimeSheetListingCard";
import EditTimeSheet from "./EditTimeSheet";
import ReviewTimeSheet from "./ReviewTimeSheet";
import "./Timesheet.scss";
import ViewTimeSheet from "./ViewTimeSheet";

/**
 * `Timesheet` is a React functional component that displays the active timesheets for a candidate.
 *
 * It provides features such as:
 * - Viewing a list of active timesheets with sorting and pagination.
 * - Viewing detailed timesheet information in a modal.
 * - Reviewing and resubmitting declined timesheets.
 * - Navigating to the timesheet history.
 *
 * The component interacts with Redux for state management, including fetching timesheet data and showing preloaders.
 * It also provides utility functions for formatting dates and times for display.
 *
 * @component
 * @returns {JSX.Element} The rendered Timesheet component.
 *
 * @remarks
 * - Uses Redux hooks (`useDispatch`, `useAppSelector`) for state and actions.
 * - Handles pagination, sorting, and modal state internally.
 * - Relies on several child components: `ActiveTimeSheetListingCard`, `ViewTimeSheet`, `ReviewTimeSheet`, and `PaginationControls`.
 * - Expects timesheet data to conform to the `TTimesheetVacancyData` type.
 */
const Timesheet: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const candidateActiveTimesheetList = useAppSelector(
    (state) => state.timesheet.candidateActiveTimesheetList
  );
  const totalCandidateActiveTimeSheet = useAppSelector(
    (state) => state.timesheet.totalCandidateActiveTimeSheet
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<PaginationLink[]>([]);
  const [sortBy, setSortBy] = useState<string>("centre");
  const [sortOrder, setSortOrder] = useState<string>("asc");
  const [itemPerPageList, setItemPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [currentTimeSheet, setCurrentTimeSheet] =
    useState<TTimesheetVacancyData | null>(null);
  const [currentReviewTimeSheet, setCurrentReviewTimeSheet] =
    useState<TTimesheetVacancyData | null>(null);
  const [currentEditTimeSheet, setCurrentEditTimeSheet] =
    useState<TTimesheetVacancyData | null>(null);

  const [showViewTimesheetModal, setShowViewTimesheetModal] =
    useState<boolean>(false);
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const handleTimesheetHistory = () => {
    navigate(AUTH_ROUTE.TIMESHEET_HISTORY);
  };

  /**
   * Loads the active timesheet data for the candidate.
   * This function dispatches an action to fetch the timesheet data from the server,
   */
  const loadActiveTimesheet = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getCandidateActiveTimeSheet({
        page: currentPage,
        per_page: itemPerPageList,
        sort_order: sortOrder,
        sort_by: sortBy,
      })
    );
    dispatch(hideTransparentPreloaderAction());
  }, [dispatch, currentPage, itemPerPageList, sortOrder, sortBy]);

  useEffect(() => {
    loadActiveTimesheet();
  }, [currentPage, itemPerPageList, loadActiveTimesheet]);

  useEffect(() => {
    if (candidateActiveTimesheetList) {
      setTotalPages(candidateActiveTimesheetList?.last_page || 1);
      setPaginationLinks(candidateActiveTimesheetList?.links);
    }
  }, [candidateActiveTimesheetList]);

  const handleFilterChange = () => {
    setShowMobileFilters((prev) => !prev);
  };
  /**
   * Function to format job start and end times into a readable string.
   * It converts the start and end times to a specific format including day, date, month, year, and time in 12-hour format.
   * @param job_start_time
   * @param job_end_time
   * @returns
   */
  const formatJobTime = (
    job_start_time: string | null,
    job_end_time: string | null
  ): string => {
    if (!job_start_time || !job_end_time) return "";

    // ✅ Trim microseconds to milliseconds (3 digits) or remove entirely
    const cleanISOString = (iso: string) =>
      iso.replace(/\.\d{6}Z$/, ".000Z").replace(/\.\d{3}Z$/, "Z");

    const start = new Date(cleanISOString(job_start_time));
    const end = new Date(cleanISOString(job_end_time));

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";

    const day = start.toLocaleString("en-US", {
      weekday: "short",
      timeZone: "UTC",
    });
    const date = start.getUTCDate();
    const month = start.toLocaleString("en-US", {
      month: "short",
      timeZone: "UTC",
    });
    const year = start.getUTCFullYear();

    // Format time in 12-hour format with AM/PM
    const formatTime = (dateObj: Date) => {
      let hours = dateObj.getUTCHours();
      const minutes = dateObj.getUTCMinutes();
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12 || 12;
      return `${hours}:${minutes.toString().padStart(2, "0")}${ampm}`;
    };

    const startTime = formatTime(start);
    const endTime = formatTime(end);

    return `${day} ${date} ${month} ${year}, ${startTime} to ${endTime}`;
  };

  /**
   * Handles the change in items per page for pagination.
   * It updates the number of items displayed per page and resets the current page to 1.
   * @param value - The new number of items per page.
   */
  const handlePageSizeChange = (value: string | number) => {
    setItemPerPageList(Number(value));
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  /**
   * Handles sorting of the timesheet data based on the specified type.
   * @param type - The type of sorting to apply (e.g., "centre", "time_frame").
   */
  const handleTimeFrameSort = (type: string) => {
    setSortBy(type);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  /**
   * Handles the change in pagination page.
   * It updates the current page based on the URL provided.
   * @param url
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  /**
   * Extracts the time from a date string and formats it into either 24-hour or 12-hour format.
   * If the date string is null or undefined, it returns an empty string.
   * @param dateString
   * @param isValue24HourFormat
   * @returns
   */
  const getTimeFromDate = (
    dateString?: string | null,
    isValue24HourFormat: boolean = false
  ) => {
    if (!dateString) return ""; // Handle null or undefined dateString
    const date = new Date(dateString);
    let hours = date.getUTCHours();
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    if (isValue24HourFormat) {
      // Return in 24-hour format (HH:mm)
      return `${hours.toString().padStart(2, "0")}:${minutes}`;
    } else {
      const ampm = hours >= 12 ? "pm" : "am";
      let displayHours = hours % 12;
      displayHours = displayHours ? displayHours : 12; // the hour '0' should be '12'
      return `${displayHours}:${minutes}${ampm}`;
    }
  };

  /**
   * Extracts the date from a date string in ISO format (YYYY-MM-DDTHH:mm:ssZ).
   * It returns only the date part (YYYY-MM-DD).
   * @param dateString
   * @returns
   */
  const getDateFromDateString = (dateString?: string | null) => {
    if (!dateString) return ""; // Handle null or undefined dateString
    return dateString.split("T")[0];
  };

  /**
   * Converts a date and time string into a UTC formatted date-time string.
   * It combines the date and time strings, parses them into a Date object, and returns the ISO string format.
   * @param datestr
   * @param timestr
   * @returns
   */
  const getUtcFormatDateTime = (
    datestr: string | null,
    timestr: string | null
  ): string => {
    if (!datestr && !timestr) return "";
    // Combine date and time into a single string
    const combined = `${datestr ?? ""} ${timestr ?? ""}`.trim();
    // Parse using Date
    const date = new Date(combined + " UTC");
    // Return ISO string in the required format
    return isNaN(date.getTime()) ? "" : date.toISOString();
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth < 991);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 991);
    }

    window.addEventListener("resize", handleResize);

    // Clean up listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLocalSortChange = (field: string, order: SortOrder) => {
    timesheetFilterOptions.filter((option) => {
      if (option.label === field) {
        handleTimeFrameSort(option.value as string);
        return true;
      }
      return false;
    });
  };

  const showHideInfo = useMemo(() => {
    if (candidateActiveTimesheetList) {
      return candidateActiveTimesheetList.data.some(
        (item) => item.status_id === 4
      );
    }
  }, [candidateActiveTimesheetList]);

  return (
    
      <div className="d-flex flex-column gap-20">
        {showHideInfo ? (
          <div className="d-flex">
            <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 align-items-center fs-13 fw-400">
              <Warning />
              Please submit your timesheets and review any declined submissions
              to continue using the portal and accepting new jobs.
            </div>
          </div>
        ) : (
          ""
        )}
        <div className="d-flex flex-column gap-10">
          <div className="d-flex flex-column">
            <div className="d-flex gap-10 align-items-center overflow-auto mobile-scroll-hide">
              <div
                className={`top-radius-10px ${
                  isMobile ? "py-13px" : "py-5px"
                } px-15px d-flex gap-5 align-items-center text-nowrap cursor-pointer bg-black text-white`}
              >
                <div className="fs-22 fs-mobile-16 fw-700">
                  {totalCandidateActiveTimeSheet}
                </div>
                <div className="fs-22 fs-mobile-16 fw-400">
                  Active Timesheet
                  {totalCandidateActiveTimeSheet !== 1 ? "s" : ""}
                </div>
              </div>
              <div
                className={`top-radius-10px ${
                  isMobile ? "py-13px" : "py-5px"
                } px-15px d-flex gap-5 align-items-center text-nowrap cursor-pointer bg-off-white text-black`}
                onClick={handleTimesheetHistory}
              >
                <div className="fs-22 fs-mobile-16 fw-400">History</div>
              </div>
            </div>
            <div className="bg-black w-100 h-5px"></div>
          </div>

          {/* <div className="d-flex d-lg-none flex-column">
            <div className="h-10px"></div>
            <div
              className="d-flex gap-5 align-items-center justify-content-center"
              style={{ cursor: "pointer" }}
              onClick={handleFilterChange}
            >
              <SortAndFilter />
              <div className="fs-13 fw-700 text-black">Sort and Filter</div>
            </div>
            <div className="h-10px"></div>
            <div className="bg-off-white min-h-1px"></div>
          </div> */}
          <div className="d-flex d-lg-none flex-column">
            <div className="h-10px"></div>
            <div className="d-flex gap-5 align-items-center justify-content-center fs-13 text-black">
              <div className="fw-400">Sort By</div>
              <div
                className="fw-700"
                onClick={() => handleTimeFrameSort("time_frame")}
              >
                Job Timeframe
              </div>
              <SortBy sort_order={sortOrder} fill="#167E76" />
            </div>
            <div className="h-10px"></div>
          </div>

          <div className="d-flex flex-column">
            {candidateActiveTimesheetList &&
              Array.isArray(candidateActiveTimesheetList.data) &&
              candidateActiveTimesheetList.data.length > 0 && (
                <>
                  <div className="d-none d-lg-flex gap-20 py-10px px-15px border-bottom border-off-white fs-13 fw-700 text-black">
                    <div className="min-w-50px"></div>
                    <div
                      className="flex-grow-1 d-flex gap-5 align-items-center"
                      onClick={() => handleTimeFrameSort("centre")}
                    >
                      Centre
                      <SortBy
                        fill={sortBy === "centre" ? "#167E76" : "#E0E0E0"}
                        sort_order={sortBy === "centre" ? sortOrder : ""}
                      />
                    </div>
                    <div
                      className="min-w-250px d-flex gap-5 align-items-center"
                      onClick={() => handleTimeFrameSort("time_frame")}
                    >
                      Job Timeframe
                      <SortBy
                        fill={sortBy === "time_frame" ? "#167E76" : "#E0E0E0"}
                        sort_order={sortBy === "time_frame" ? sortOrder : ""}
                      />
                    </div>
                    <div className="min-w-150px"></div>
                    <div className="min-w-130px"></div>
                  </div>
                  <div
                    className={`d-block d-lg-none ${
                      showMobileFilters ? "d-flex" : "d-none"
                    }`}
                  >
                    <SortFilterDropdown
                      sortFields={["Centre", "Job Timeframe"]}
                      defaultField="Centre"
                      onChange={handleLocalSortChange}
                    />
                  </div>
                </>
              )}

            {candidateActiveTimesheetList &&
              Array.isArray(candidateActiveTimesheetList.data) &&
              candidateActiveTimesheetList.data.length > 0 &&
              candidateActiveTimesheetList.data.map(
                (_timesheet: TTimesheetVacancyData, _index: number) => (
                  <div key={_index}>
                    <ActiveTimeSheetListingCard
                      formatJobTime={formatJobTime}
                      _timesheet={_timesheet}
                      showViewTimesheetModal={showViewTimesheetModal}
                      setShowViewTimesheetModal={setShowViewTimesheetModal}
                      setCurrentTimeSheet={setCurrentTimeSheet}
                      setCurrentReviewTimeSheet={setCurrentReviewTimeSheet}
                      setShowReviewModal={setShowReviewModal}
                      setShowEditModal={setShowEditModal}
                      setCurrentEditTimeSheet={setCurrentEditTimeSheet}
                    />
                  </div>
                )
              )}

            {candidateActiveTimesheetList &&
              Array.isArray(candidateActiveTimesheetList.data) &&
              candidateActiveTimesheetList.data.length === 0 && (
                <>
                  <div className="d-flex flex-column gap-20 align-items-center pt-80px">
                    <div className="bg-teal-light w-60px h-60px rounded-circle d-flex align-items-center justify-content-center">
                      <RoundedTick size={33} />
                    </div>
                    <div className="fs-30 fw-400 text-black">
                      All caught up!
                    </div>
                    <div className="fs-16 fw-400 text-black">
                      No timesheets pending right now.
                    </div>
                  </div>
                </>
              )}

            <ViewTimeSheet
              formatJobTime={formatJobTime}
              getDateFromDateString={getDateFromDateString}
              getTimeFromDate={getTimeFromDate}
              getUtcFormatDateTime={getUtcFormatDateTime}
              timeSheetData={currentTimeSheet}
              showViewTimesheetModal={showViewTimesheetModal}
              setShowViewTimesheetModal={setShowViewTimesheetModal}
            />

            <ReviewTimeSheet
              formatJobTime={formatJobTime}
              getDateFromDateString={getDateFromDateString}
              getTimeFromDate={getTimeFromDate}
              getUtcFormatDateTime={getUtcFormatDateTime}
              timeSheetData={currentReviewTimeSheet}
              showReviewModal={showReviewModal}
              setShowReviewModal={setShowReviewModal}
              loadActiveTimesheet={loadActiveTimesheet}
            />

            <EditTimeSheet
              getDateFromDateString={getDateFromDateString}
              getTimeFromDate={getTimeFromDate}
              getUtcFormatDateTime={getUtcFormatDateTime}
              timeSheetData={currentEditTimeSheet}
              showEditModal={showEditModal}
              setShowEditModal={setShowEditModal}
              loadActiveTimesheet={loadActiveTimesheet}
            />

            {/* Pagination Component */}
            {candidateActiveTimesheetList &&
              totalCandidateActiveTimeSheet !== null &&
              totalCandidateActiveTimeSheet > DEFAULT_ITEMS_PER_PAGE && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  paginationLinks={paginationLinks}
                  itemsPerPage={itemPerPageList}
                  itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handlePageSizeChange}
                />
              )}
            {/* Pagination Component */}
          </div>
        </div>
      </div>
    
  );
};

export default Timesheet;
